import React from 'react';
import { formatTime } from '../../utils/timeUtils';

export default function BreakTimer({ remaining, durationMs, isLongBreak, onSkip }) {
  const timeStr = formatTime(remaining);
  const progress = durationMs > 0 ? Math.min(1 - remaining / durationMs, 1) : 0;
  const progressPercent = Math.round(progress * 100);

  return (
    <div
      role="main"
      aria-label="Break period"
      className="relative flex flex-col items-center justify-between min-h-[100dvh] px-4 py-4 max-w-md mx-auto"
    >
      <button
        type="button"
        onClick={onSkip}
        aria-label="Skip break"
        className="absolute top-3 right-3 z-10 px-3 py-1.5 rounded-full text-xs font-medium text-[color:var(--color-accent)] bg-transparent hover:bg-tomato-500/10 transition-colors"
      >
        Skip break
      </button>

      <div className="flex-1 flex items-center justify-center">
        <svg viewBox="0 0 200 200" width="200" height="200" aria-hidden="true">
          <ellipse cx="100" cy="170" rx="50" ry="8" fill="#3d2010" opacity="0.3" />
          <path d="M100 168 Q98 130 102 100 Q104 80 100 65" stroke="#2d5016" strokeWidth="3.5" fill="none" strokeLinecap="round" />
          <ellipse cx="82" cy="110" rx="14" ry="6" fill="#4ade80" opacity="0.7" transform="rotate(25 82 110)" />
          <ellipse cx="118" cy="100" rx="14" ry="6" fill="#86efac" opacity="0.6" transform="rotate(-20 118 100)" />
          <circle cx="100" cy="60" r="16" fill="#ef4444" opacity="0.6" />
          <circle cx="100" cy="60" r="14" fill="#ef4444" opacity="0.4" />
          <path d="M93 57 Q95 60 97 57" stroke="#7f1d1d" strokeWidth="1.5" fill="none" strokeLinecap="round" />
          <path d="M103 57 Q105 60 107 57" stroke="#7f1d1d" strokeWidth="1.5" fill="none" strokeLinecap="round" />
          <text x="125" y="45" fill="currentColor" fontSize="14" fontWeight="700" opacity="0.6" className="text-[color:var(--color-text-muted)]">z</text>
          <text x="135" y="32" fill="currentColor" fontSize="11" fontWeight="700" opacity="0.4" className="text-[color:var(--color-text-muted)]">z</text>
          <text x="142" y="22" fill="currentColor" fontSize="9" fontWeight="700" opacity="0.3" className="text-[color:var(--color-text-muted)]">z</text>
          <circle cx="45" cy="30" r="3" fill="currentColor" opacity="0.3" className="text-amber-400" />
          <circle cx="160" cy="45" r="2" fill="currentColor" opacity="0.2" className="text-amber-400" />
          <circle cx="35" cy="55" r="1.5" fill="currentColor" opacity="0.15" className="text-amber-400" />
        </svg>
      </div>

      <div className="w-full space-y-3 pb-6 text-center">
        <h2 className="text-2xl font-bold text-[color:var(--color-text-primary)]">
          {isLongBreak ? 'Long Break' : 'Short Break'}
        </h2>
        <p className="text-sm text-[color:var(--color-text-secondary)]">
          {isLongBreak
            ? 'Great work! Take a proper rest.'
            : 'Your plant is resting. You should too.'}
        </p>

        <div
          aria-live="polite"
          aria-atomic="true"
          className="text-6xl font-bold tracking-tight tabular-nums bg-gradient-to-br from-[color:var(--color-text-primary)] to-[color:var(--color-text-secondary)] bg-clip-text text-transparent"
        >
          {timeStr}
        </div>

        <div
          role="progressbar"
          aria-valuenow={progressPercent}
          aria-valuemin="0"
          aria-valuemax="100"
          className="h-1.5 rounded-full bg-[color:var(--color-bg-elevated)] overflow-hidden"
        >
          <div
            className="h-full rounded-full bg-gradient-to-r from-cream-300 to-cream-500 transition-[width] duration-1000"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>
    </div>
  );
}
